import { useParams } from "react-router-dom";

import { useTill } from "~/api/tills/hooks/use-till";
import { ReportStatus } from "~/components";
import { useSalesReport } from "~/hooks/queries/reports";

import {
  CashDrawerSection,
  DepositFeeSummarySection,
  DiscountsSection,
  PaymentSummarySection,
  RefundsSection,
  SalesByCategoriesSection,
  SalesSummarySection,
  SalesPerUsersSection,
  TransactionSummarySection,
  VatSummarySection
} from "./sections";
import { hasDepositFees, hasDiscounts } from "./sections/utils";
import { useReportSections } from "./use-report-sections";

export const ReportList = () => {
  const { sessionId } = useParams();

  const { data: report, status: reportStatus } = useSalesReport(sessionId);

  if (reportStatus !== "success") {
    return <ReportStatus status={reportStatus} />;
  }

  return <ReportListContent report={report.report} receipt={report.receipt} />;
};

type ReportListContentProps = {
  report: NonNullable<ReturnType<typeof useSalesReport>["data"]>["report"];
  receipt: NonNullable<ReturnType<typeof useSalesReport>["data"]>["receipt"];
};

const ReportListContent = ({ report, receipt }: ReportListContentProps) => {
  const { data: till } = useTill(report.generated.tillId);
  const sections = useReportSections(report, receipt, till?.currency);
  const tillTakesCash = till?.paymentProviders.includes("cash") ?? false;

  return (
    <div className="flex flex-col gap-6 pb-0">
      <SalesSummarySection data={sections.salesSummary} />
      {hasDiscounts(sections.discounts) && (
        <DiscountsSection data={sections.discounts} />
      )}
      <RefundsSection data={sections.refunds} />
      <VatSummarySection data={sections.vatSummary} />
      {hasDepositFees(sections.depositFeeSummary) && (
        <DepositFeeSummarySection data={sections.depositFeeSummary} />
      )}
      <PaymentSummarySection data={sections.paymentSummary} />
      {tillTakesCash ? <CashDrawerSection data={sections.cashDrawer} /> : null}
      <TransactionSummarySection data={sections.transactionSummary} />
      <SalesByCategoriesSection data={sections.salesByCategories} />
      <SalesPerUsersSection data={sections.salesPerUsers} />
    </div>
  );
};
